// components/RagAiQueAns.tsx
import { Button, Input, Card, CardBody } from "@heroui/react";
import { useMutation } from "@tanstack/react-query";
import { ListCommanWay } from "API/commonApi";
import React, { useEffect, useState } from "react";
import UseToast from "utils/AfToast";
import { Bar, Line, Pie, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement);

const RagAiQueAns: React.FC = () => {
  const [question, setQuestion] = useState("");
  const [textAnswer, setTextAnswer] = useState("");
  const [vizCode, setVizCode] = useState(""); // full <DataViz>...</DataViz>
  const [VizComponent, setVizComponent] = useState<React.FC | null>(null);
  const [queryData, setQueryData] = useState<any[]>([]); // ← this is the key!

  // Reset on new question
  useEffect(() => {
    setTextAnswer("");
    setVizCode("");
    setVizComponent(null);
    setQueryData([]);
  }, [question]);

  const { mutate: mutateAskRagQue, isLoading } = useMutation({
    mutationFn: () => ListCommanWay("/admin/rag/ask-que", { que: question }, {}),

    onSuccess(data) {
      if (data.status !== "SUCCESS") {
        UseToast(data.message || "Error", "error");
        return;
      }

      const fullText = data?.data?.answer || "";
      const rawData = data?.data?.data || []; // ← your actual DB result (array)

      setTextAnswer(fullText || "No response.");

      // Save the real data for the viz
      setQueryData(rawData);
      return;

      // Extract text part
      const vizStart = fullText.indexOf("<DataViz>");
      const textPart = vizStart === -1 ? fullText.trim() : fullText.slice(0, vizStart).trim();
      setTextAnswer(textPart || "No response.");

      // Extract viz code
      const vizMatch = fullText.match(/<DataViz>([\s\S]*?)<\/DataViz>/i);
      if (vizMatch && vizMatch[1].trim().length > 20) {
        setVizCode("<DataViz>" + vizMatch[1].trim() + "</DataViz>");
      } else {
        setVizCode("");
      }
    },

    onError(err: any) {
      UseToast(err?.message || "Something went wrong", "error");
    },
  });

  useEffect(() => {
    // Prevent loading Chart.js multiple times
    if (!document.getElementById("chartjs-lib")) {
      const script = document.createElement("script");
      script.src = "https://cdn.jsdelivr.net/npm/chart.js";
      script.id = "chartjs-lib";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    if (textAnswer) {
      injectHTMLWithScripts(textAnswer, "ai-output-container");
    }
  }, [textAnswer]);

  return (
    <div className="px-2 py-4">
      <h1 className="text-center mb-2 text-2xl font-bold">RAG AI Q&A</h1>

      <div className="flex justify-center gap-4 mb-8 [&_input]:border-none [&_input]:px-0">
        <Input
          label="Question"
          variant="bordered"
          labelPlacement="outside-left"
          placeholder="Ask a question..."
          classNames={{ mainWrapper: "flex-1" }}
          className="border-none w-auto flex-1"
          value={question}
          onValueChange={setQuestion}
          disabled={isLoading}
          // Enter key to submit
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              mutateAskRagQue();
            }
          }}
        />
        <Button onPress={() => mutateAskRagQue()} isLoading={isLoading} color="primary">
          Ask AI
        </Button>
      </div>

      <div className="space-y-8">
        {/* Text Answer */}
        {textAnswer && (
          <Card>
            <CardBody className="prose prose-sm max-w-none">
              <div id="ai-output-container"></div>
            </CardBody>
          </Card>
        )}

        {/* Dynamic Visualization */}
        {VizComponent && <VizComponent />}
      </div>
    </div>
  );
};

function injectHTMLWithScripts(html, containerId) {
  const container = document.getElementById(containerId);
  if (!container) return;

  // Extract and remove script tags
  const temp = document.createElement("div");
  temp.innerHTML = html;

  const scripts = temp.querySelectorAll("script");
  scripts.forEach((s) => s.parentNode.removeChild(s));

  container.innerHTML = temp.innerHTML; // Insert HTML without scripts

  // Execute scripts separately
  scripts.forEach((script) => {
    const newScript = document.createElement("script");
    if (script.src) newScript.src = script.src;
    else newScript.innerHTML = script.innerHTML;
    document.body.appendChild(newScript);
  });
}

export default RagAiQueAns;
